import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Loader2, CheckSquare, Layers, Mail, Hash, Handshake, UserCheck, Building2, Briefcase, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../lib/api';
import { useWorkspace } from '../context/WorkspaceContext';
import { cn } from '../lib/utils';

const GROUPS = [
    { key: 'task', label: 'Task', icon: CheckSquare },
    { key: 'space', label: 'Space', icon: Layers },
    { key: 'mail', label: 'Mail', icon: Mail },
    { key: 'channel', label: 'Channel', icon: Hash },
    { key: 'partnership', label: 'Contact', icon: Handshake },
    { key: 'lead', label: 'Lead', icon: UserCheck },
    { key: 'client', label: 'Client', icon: Building2 },
    { key: 'project', label: 'Project', icon: Briefcase },
];

const PER_PAGE = 5;
const DEBOUNCE_MS = 300;

export default function GlobalSearchModal({ open, onClose }) {
    const { currentWorkspace } = useWorkspace();
    const navigate = useNavigate();
    const [q, setQ] = useState('');
    const [activeGroup, setActiveGroup] = useState('all'); // 'all' | group.key
    const [page, setPage] = useState(1);
    const [results, setResults] = useState(null); // { groups: { task: {items,total}, ... } }
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);
    const debounceRef = useRef(null);
    const reqIdRef = useRef(0);

    useEffect(() => {
        if (open) {
            setQ('');
            setActiveGroup('all');
            setPage(1);
            setResults(null);
            setTimeout(() => inputRef.current?.focus(), 30);
        }
    }, [open]);

    useEffect(() => {
        if (!open || !currentWorkspace?.id) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!q.trim()) { setResults(null); setLoading(false); return; }
        setLoading(true);
        debounceRef.current = setTimeout(async () => {
            const myId = ++reqIdRef.current;
            try {
                const params = { q, workspace_id: currentWorkspace.id, page, per_page: PER_PAGE };
                if (activeGroup !== 'all') params.group = activeGroup;
                const res = await api.get('/search', { params });
                if (myId === reqIdRef.current) setResults(res.data);
            } catch (e) {
                if (myId === reqIdRef.current) setResults(null);
            } finally {
                if (myId === reqIdRef.current) setLoading(false);
            }
        }, DEBOUNCE_MS);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [q, activeGroup, page, open, currentWorkspace?.id]);

    // Reset page when query or group changes
    useEffect(() => { setPage(1); }, [q, activeGroup]);

    // ESC to close
    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, onClose]);

    if (!open) return null;

    const go = (url) => { onClose(); navigate(url); };
    const groupsData = results?.groups || {};
    const groupsToRender = activeGroup === 'all'
        ? GROUPS.filter(g => (groupsData[g.key]?.total || 0) > 0)
        : GROUPS.filter(g => g.key === activeGroup);
    const totalAll = Object.values(groupsData).reduce((s, v) => s + (v?.total || 0), 0);

    return (
        <div
            className="fixed inset-0 z-[100] bg-black/40 flex items-start justify-center pt-[8vh]"
            onClick={onClose}
            data-testid="global-search-modal"
        >
            <div
                className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header: group label + input + close */}
                <div className="flex items-center gap-3 px-4 py-3 border-b">
                    <Search className="w-5 h-5 text-brand-text-muted" />
                    <span className="text-sm font-semibold uppercase tracking-wide text-brand-text-muted">
                        {activeGroup === 'all' ? 'ALL' : GROUPS.find(g => g.key === activeGroup)?.label}
                    </span>
                    <span className="text-brand-text-muted">|</span>
                    <input
                        ref={inputRef}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Cari task, space, mail, channel, CRM..."
                        className="flex-1 bg-transparent border-0 outline-none text-base"
                        data-testid="global-search-input"
                    />
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-full hover:bg-brand-accent-soft"
                        data-testid="global-search-close-btn"
                        aria-label="Tutup"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Group filter chips */}
                <div className="flex items-center gap-1 px-3 py-2 border-b overflow-x-auto">
                    <Chip active={activeGroup === 'all'} onClick={() => setActiveGroup('all')} testid="chip-all">
                        All{results ? ` (${totalAll})` : ''}
                    </Chip>
                    {GROUPS.map((g) => {
                        const count = groupsData[g.key]?.total || 0;
                        return (
                            <Chip
                                key={g.key}
                                active={activeGroup === g.key}
                                onClick={() => setActiveGroup(g.key)}
                                icon={g.icon}
                                testid={`chip-${g.key}`}
                            >
                                {g.label}{results ? ` (${count})` : ''}
                            </Chip>
                        );
                    })}
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto">
                    {!q.trim() ? (
                        <div className="p-12 text-center text-brand-text-muted text-sm">
                            Mulai ketik untuk mencari di seluruh workspace
                        </div>
                    ) : loading && !results ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
                        </div>
                    ) : groupsToRender.length === 0 ? (
                        <div className="p-12 text-center text-brand-text-muted text-sm">
                            Tidak ada hasil untuk "<span className="font-medium">{q}</span>"
                        </div>
                    ) : (
                        <div>
                            {groupsToRender.map((g) => {
                                const data = groupsData[g.key] || { items: [], total: 0 };
                                return (
                                    <GroupSection
                                        key={g.key}
                                        group={g}
                                        data={data}
                                        onNavigate={go}
                                        onSeeAll={() => setActiveGroup(g.key)}
                                        singleGroupMode={activeGroup !== 'all'}
                                    />
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Pagination (only when single group + multiple pages) */}
                {q.trim() && activeGroup !== 'all' && results && groupsData[activeGroup]?.total > PER_PAGE && (
                    <div className="flex items-center justify-between px-4 py-2 border-t text-sm">
                        <div className="text-brand-text-muted">
                            Halaman {page} dari {Math.ceil((groupsData[activeGroup]?.total || 0) / PER_PAGE)}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={page <= 1}
                                className="p-1.5 rounded hover:bg-brand-accent-soft disabled:opacity-40"
                                data-testid="search-page-prev"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => setPage((p) => p + 1)}
                                disabled={page * PER_PAGE >= (groupsData[activeGroup]?.total || 0)}
                                className="p-1.5 rounded hover:bg-brand-accent-soft disabled:opacity-40"
                                data-testid="search-page-next"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function Chip({ children, active, onClick, icon: Icon, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap',
                active
                    ? 'bg-brand-primary text-white'
                    : 'bg-brand-accent-soft text-brand-text-secondary hover:bg-brand-primary/20'
            )}
            data-testid={testid}
        >
            {Icon && <Icon className="w-3 h-3" />}
            {children}
        </button>
    );
}

function GroupSection({ group, data, onNavigate, onSeeAll, singleGroupMode }) {
    const Icon = group.icon;
    if (!data.total) return null;
    return (
        <div className="border-b last:border-b-0" data-testid={`group-${group.key}`}>
            <div className="flex items-center justify-between px-4 pt-3 pb-1">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-brand-text-muted">
                    <Icon className="w-3.5 h-3.5" />
                    {group.label}
                    <span className="text-brand-text-muted/70 normal-case">({data.total})</span>
                </div>
                {!singleGroupMode && data.total > PER_PAGE && (
                    <button
                        onClick={onSeeAll}
                        className="text-xs text-brand-primary hover:underline"
                        data-testid={`see-all-${group.key}`}
                    >
                        Lihat semua
                    </button>
                )}
            </div>
            <div>
                {data.items.map((item) => (
                    <button
                        key={item.id}
                        onClick={() => onNavigate(item.url)}
                        className="w-full flex items-start gap-3 px-4 py-2.5 hover:bg-brand-accent-soft/50 text-left"
                        data-testid={`search-result-${group.key}-${item.id}`}
                    >
                        <Icon className="w-4 h-4 mt-1 text-brand-primary flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{item.title || '(tanpa judul)'}</div>
                            {item.subtitle && (
                                <div className="text-xs text-brand-text-muted truncate">{item.subtitle}</div>
                            )}
                        </div>
                        {item.extra && (
                            <span className="text-xs px-2 py-0.5 bg-brand-accent-soft rounded-full text-brand-text-muted flex-shrink-0">
                                {item.extra}
                            </span>
                        )}
                    </button>
                ))}
            </div>
        </div>
    );
}
